import { type FC, useEffect, useState } from "react";

import { useIsAssessing } from "../section/assessment/hooks";

import { AutoFollowButtons } from "./AutoFollowButtons";
import { FollowButton } from "./FollowButton";
import { MovePresentationButton } from "./MovePresentationButton";
import { ProjectingButton } from "./ProjectingButton";
import { StopPresentingButton } from "./StopPresentingButton";
import { openProjectorView } from "./projector/projector-messaging";

import { withActiveAv } from "components/av/AvSubsystemLoader";
import { PresentationBanner } from "components/footer/PresentationBanner";
import { PagePresentingMenu } from "components/materials/presentation/PagePresentingMenu";
import { ReturnToDefaultTabButton } from "components/materials/presentation/ReturnToDefaultTabButton";
import { StopFollowingButton } from "components/materials/presentation/StopFollowingButton";
import { TakeOverPresentationButton } from "components/materials/presentation/TakeOverPresentationButton";
import { PageType } from "components/server-types";
import { useIsLgOrLarger, useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { selectCurrentUserId, selectIsPracticeClassSession } from "store/selectors";
import { selectCurrentClassUserIsPerformingAvCheck } from "store/selectors/class-user";
import { selectIsObservingVirtualClass } from "store/selectors/pubsub-virtual-class-state";
import { useJoinAsCourseAdmin } from "utils/hooks/use-join-as-section-admin";
import {
  presentSession,
  setPresentationWindow,
  startProjecting,
  stopPresenting,
  usePresentation,
  usePresentationBannerColoring,
  usePresentationBannerText,
} from "utils/presentation";
import { useViewLevel } from "utils/urls";

const LazyScreenshareButton = withActiveAv(() => import("components/av/ScreenshareButton"));

export type PresentOptions = {
  isRollup?: boolean;
  hasProjectorView?: boolean;
  isPractice?: boolean;
  accessId?: string;
};

export type PresentationOptionsProps = {
  selectedPage: PageType;
  isInClassSession?: boolean;
};

export const PresentationOptions: FC<PresentationOptionsProps> = ({
  selectedPage,
  isInClassSession = false,
}) => {
  const isLargeScreen = useIsLgOrLarger();
  const showShortText = !useIsMdOrLarger();
  const {
    presentedSessionId,
    presentedPageId,
    isFollowing,
    isPresenting,
    isPresentingUser,
    unfeaturedTab,
    courseId,
    isFollowingDisconnected,
  } = usePresentation();
  const { joinAsCourseAdminIfNew } = useJoinAsCourseAdmin();
  const isObservingClass = useAppSelector(selectIsObservingVirtualClass);
  const isPracticeClassSession = useAppSelector(selectIsPracticeClassSession);
  const isPerformingAvCheck = useAppSelector(selectCurrentClassUserIsPerformingAvCheck);

  const viewLevel = useViewLevel();

  const isAssessing = useIsAssessing();
  const { canAuthorCourse, canParticipateInCourse, canPresentCourse } = useCapabilities();
  const userId = useAppSelector(selectCurrentUserId);

  const isPageView = viewLevel.page;
  const { bannerText, tooltipText } = usePresentationBannerText(viewLevel, showShortText);
  const color = usePresentationBannerColoring();

  // Track if user has ever followed this presentation session - if so, don't auto-follow again
  const [hasFollowedThisSession, setHasFollowedThisSession] = useState(false);
  useEffect(() => {
    if (isFollowing) {
      setHasFollowedThisSession(true);
    }
  }, [isFollowing]);

  // Reset when presentation ends
  useEffect(() => {
    if (!presentedPageId) {
      setHasFollowedThisSession(false);
    }
  }, [presentedPageId]);

  const onPresent = async (options: PresentOptions = {}) => {
    await joinAsCourseAdminIfNew();

    setPresentationWindow();
    return presentSession(presentedSessionId, { page: selectedPage, ...options, userId });
  };

  const onStopPresenting = () => {
    return stopPresenting(presentedSessionId);
  };

  const onProjecting = () => {
    openProjectorView(courseId);
    return startProjecting(presentedSessionId, {
      // We add these fields to ensure the server validation passes,
      // just in case the projecting action is taken before any presenting action.
      presented_page_id: selectedPage.id,
      presenting_user_id: userId,
    });
  };

  if (!presentedPageId) {
    if (canPresentCourse && isPageView) {
      return (
        <PagePresentingMenu
          isDisabled={selectedPage?.category === "instructor_workspace" || isAssessing}
          isPractice={isPracticeClassSession}
          size="m"
          onPractice={() => onPresent({ isPractice: true })}
          onPresent={onPresent}
        />
      );
    }

    return null;
  }

  if (isPresenting) {
    return (
      <>
        <PresentationBanner bannerText={bannerText} color={color} tooltipText={tooltipText}>
          <LazyScreenshareButton color={color} />
          <ReturnToDefaultTabButton color={color} />
          {canPresentCourse && (
            <StopPresentingButton
              color={color}
              eventName={
                isPracticeClassSession ? "Presentation - End practice" : "Presentation - End"
              }
              showShortText={showShortText || isPracticeClassSession}
              onStopPresenting={onStopPresenting}
            />
          )}
        </PresentationBanner>

        {!unfeaturedTab && isLargeScreen && <ProjectingButton onProject={onProjecting} />}
      </>
    );
  }

  const isFollowingAsAStudent = isFollowing && canParticipateInCourse;
  const isNotFollowingThePresenter = !isFollowing && !isPresentingUser;

  // Only show auto-follow if user hasn't already followed during this presentation session
  // and is not currently in AV check
  const shouldAutoFollow = isInClassSession && !hasFollowedThisSession && !isPerformingAvCheck;

  // Not presenting at all, or at least not presenting in this tab given the presentations
  // are tied to a specific tab.
  return (
    <PresentationBanner bannerText={bannerText} color={color} tooltipText={tooltipText}>
      {canAuthorCourse && <LazyScreenshareButton color={color} />}

      {canPresentCourse && !isObservingClass && <TakeOverPresentationButton color={color} />}

      {isNotFollowingThePresenter && <AutoFollowButtons allowAutoFollow={shouldAutoFollow} />}

      {isFollowingAsAStudent && isFollowingDisconnected && (
        <FollowButton color={color} showReturnText />
      )}

      {isFollowingAsAStudent && !isFollowingDisconnected && <StopFollowingButton color={color} />}

      {isPresentingUser && isPageView && (
        <MovePresentationButton
          color={color}
          onClick={() =>
            onPresent({ accessId: null, isRollup: false, isPractice: isPracticeClassSession })
          }
        />
      )}

      {isPresentingUser && (
        <StopPresentingButton
          color={color}
          eventName={isPracticeClassSession ? "Presentation - End practice" : "Presentation - End"}
          onStopPresenting={onStopPresenting}
        />
      )}
    </PresentationBanner>
  );
};
